/**
 * The drawer's People (TD-103, DD-053): the tags. The owner has its own
 * field above, "Assegnato a". In edit mode a tag the viewer may change has
 * a remove control, and a picker adds people. Team viewers see whether the
 * client sees the task.
 */

import { OptionDropdown } from "@/components/ui/OptionDropdown";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useLocale } from "@/hooks/useLocale";
import { TaskAssigneePill } from "../components/TaskRowParts";
import { assigneePillStyle } from "../taskAssigneeConfig";
import type { TaskPerson } from "../types";

const ClientEntityIcon = ENTITY_CONFIG.client.icon;

interface TaskPeopleSectionProps {
  tags: TaskPerson[];
  /** Shows "Visibile al cliente". Team viewers only. */
  clientVisible: boolean;
  /** Edit mode: the people the picker offers. Omitted in view mode. */
  options?: TaskPerson[];
  onAdd?: (person: TaskPerson) => void;
  /** Edit mode: whether a tag shows its remove control. */
  canRemove?: (person: TaskPerson) => boolean;
  onRemove?: (personId: string) => void;
}

export function TaskPeopleSection({
  tags,
  clientVisible,
  options = [],
  onAdd,
  canRemove,
  onRemove,
}: TaskPeopleSectionProps) {
  const { t } = useLocale();
  const pickerOptions = options.map((person) => {
    const style = assigneePillStyle(person);
    return { value: person.id, label: style.label, dot: style.dot };
  });

  return (
    <div>
      <span id="task-drawer-people-label" className="block text-sm font-medium text-gray-700 mb-1">
        Persone
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.length === 0 && !onAdd && <span className="text-sm text-gray-400">Nessuno</span>}
        {tags.map((person) => (
          <TaskAssigneePill
            key={person.id}
            assignee={person}
            onRemove={onRemove && canRemove?.(person) ? () => onRemove(person.id) : undefined}
          />
        ))}
      </div>
      {onAdd && pickerOptions.length > 0 && (
        // No `portal`: a body-level menu sits outside the drawer's dialog.
        <div className="mt-2">
          <OptionDropdown
            value={null}
            options={pickerOptions}
            onChange={(personId) => {
              const person = options.find((option) => option.id === personId);
              if (person) onAdd(person);
            }}
            placeholder="Aggiungi persona"
            variant="input"
            ariaLabelledBy="task-drawer-people-label"
          />
        </div>
      )}
      {clientVisible && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500">
          <ClientEntityIcon className="w-3.5 h-3.5" />
          {t("tasks.visibleToClient")}
        </p>
      )}
    </div>
  );
}
